import { useEffect, useRef, useState, Fragment } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Instagram, Facebook, Check, Loader2 } from "lucide-react";
import confetti from "canvas-confetti";
import { api, formatApiErrorDetail } from "@/lib/api";

// Tickets go on sale the moment Thursday 10/29 night becomes Friday 10/30 — St. Louis time (CDT)
const ON_SALE = new Date("2026-10-30T00:00:00-05:00");

const NEON = "#ff2d2d";
const neonText = {
  color: "#ffd9d9",
  textShadow: `0 0 6px ${NEON}, 0 0 18px ${NEON}, 0 0 42px ${NEON}, 0 0 80px rgba(255,45,45,0.55)`,
};
const goldText = {
  background: "linear-gradient(180deg, #fbf5e4 0%, #f4e7c0 38%, #ddb878 72%, #c49a55 100%)",
  WebkitBackgroundClip: "text",
  backgroundClip: "text",
  color: "transparent",
};
const inputCls = "w-full rounded-xl bg-black/55 border border-white/25 px-5 py-4 text-sm text-[#FDFBF7] placeholder-[#8f887a] focus:outline-none focus:border-[#ff2d2d] transition-colors";

const pad = (n) => String(n).padStart(2, "0");

const Divider = ({ children, tid }) => (
  <div className="flex items-center gap-5" data-testid={tid}>
    <span className="flex-1 h-px bg-gradient-to-r from-transparent to-[#d8c9a8]/50" />
    <span className="font-didone text-[#efe0b8] text-sm sm:text-base font-semibold uppercase tracking-[0.3em] whitespace-nowrap"
      style={{ textShadow: "0 2px 14px rgba(0,0,0,0.65)" }}>
      {children}
    </span>
    <span className="flex-1 h-px bg-gradient-to-l from-transparent to-[#d8c9a8]/50" />
  </div>
);

export const ComingSoon = () => {
  const [now, setNow] = useState(() => Date.now());
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const celebrated = useRef(false);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const diff = Math.max(0, ON_SALE.getTime() - now);
  const live = diff === 0;

  // Midnight moment — classy Chocolate City celebration: gold, deep red, cream, cocoa
  useEffect(() => {
    if (!live || celebrated.current) return;
    celebrated.current = true;
    const colors = ["#D8B26E", "#B3122F", "#F4E7C0", "#8B5A2B"];
    const base = { colors, ticks: 320, gravity: 0.65, scalar: 0.85, startVelocity: 34, decay: 0.93 };
    confetti({ ...base, particleCount: 90, angle: 62, origin: { x: 0, y: 0.78 }, spread: 58 });
    confetti({ ...base, particleCount: 90, angle: 118, origin: { x: 1, y: 0.78 }, spread: 58 });
    setTimeout(() => confetti({ ...base, particleCount: 70, origin: { x: 0.5, y: 0.6 }, spread: 100, startVelocity: 26 }), 700);
    const end = Date.now() + 18000;
    const rain = setInterval(() => {
      if (Date.now() > end) return clearInterval(rain);
      confetti({ colors, particleCount: 3, startVelocity: 0, ticks: 420, gravity: 0.3, scalar: 0.65,
                 origin: { x: Math.random(), y: -0.06 }, drift: Math.random() - 0.5, shapes: ["circle"] });
    }, 420);
    return () => clearInterval(rain);
  }, [live]);
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  const secs = Math.floor((diff % 60000) / 1000);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) {
      toast.error("Enter your email to get early access.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/early-access", { email: email.trim(), phone: phone.trim(), source: "ep3-chocolate-city" });
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong — try again.");
    } finally {
      setBusy(false);
    }
  };

  const cells = [
    { label: "Days", value: days, tid: "ep3-days" },
    { label: "Hrs", value: hours, tid: "ep3-hours" },
    { label: "Min", value: mins, tid: "ep3-mins" },
    { label: "Sec", value: secs, tid: "ep3-secs" },
  ];

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#070202] flex items-center justify-center px-5 py-14" data-testid="coming-soon-page">
      {/* Clean candle-lit scene: her top-right, candles, roses, red neon */}
      <div aria-hidden="true" className="absolute inset-0 bg-cover bg-[position:right_top]"
        style={{ backgroundImage: "url(/ep3-bg.jpg?v=11)", filter: "brightness(0.6) saturate(1.05)" }} />
      {/* Foreground: full-height sharp layer so her whole figure shows, never cropped in half.
          On phones she's scaled to the top-right corner so the title never covers her face. */}
      <div aria-hidden="true" className="absolute inset-y-0 right-0 w-full bg-no-repeat bg-[length:auto_40%] sm:bg-[length:auto_100%] bg-[position:right_top]"
        style={{ backgroundImage: "url(/ep3-bg.jpg?v=11)", filter: "brightness(0.92) saturate(1.08)" }}
        data-testid="ep3-bg" />
      <div aria-hidden="true" className="absolute inset-0"
        style={{ background: "linear-gradient(90deg, rgba(7,2,2,0.55) 0%, rgba(7,2,2,0.25) 45%, rgba(7,2,2,0.05) 75%), linear-gradient(180deg, rgba(7,2,2,0.15) 0%, rgba(7,2,2,0.28) 45%, rgba(7,2,2,0.7) 92%)" }} />

      <motion.div initial={{ opacity: 0, y: 34 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        className="relative z-10 w-full max-w-md text-center">

        <img src="/logo-jayatk.png" alt="Jay Atk" className="h-12 w-auto mx-auto mb-7" data-testid="ep3-logo" />
        <div className="font-didone text-lg sm:text-xl font-bold uppercase tracking-[0.4em] text-[#f3ead8]" style={{ textShadow: "0 2px 18px rgba(0,0,0,0.7)" }}>R&B Day Party</div>
        <div className="font-didone text-[11px] font-medium uppercase tracking-[0.5em] text-[#e3d9c4] mt-2.5" style={{ textShadow: "0 2px 14px rgba(0,0,0,0.7)" }}>Episode Three</div>
        <h1 className="leading-[0.9] mt-3">
          <img src="/ep3-title.png?v=9" alt="Chocolate City" className="w-full mx-auto" data-testid="ep3-title" />
        </h1>

        {live ? (
          <div className="mt-10" data-testid="ep3-live">
            <Divider tid="ep3-live-divider">The Wait Is Over</Divider>
            <div className="font-didone font-black text-4xl sm:text-5xl tracking-[0.06em] mt-8" style={neonText}>TICKETS ARE LIVE</div>
            <a href="/" data-testid="ep3-get-tickets"
              className="mt-9 inline-block rounded-full border-2 border-[#D8B26E] bg-[#D8B26E] text-[#1a0a05] px-12 py-4 text-sm font-bold uppercase tracking-[0.35em] transition-all duration-300 hover:bg-transparent hover:text-[#D8B26E]"
              style={{ boxShadow: "0 0 28px rgba(216,178,110,0.4)" }}>
              Get Tickets
            </a>
          </div>
        ) : (
          <div className="mt-8" data-testid="ep3-countdown">
            <Divider tid="ep3-sale-divider">Tickets Go On Sale</Divider>
            <div className="relative mt-7 font-didone">
              <div aria-hidden="true" className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[135%] h-[230%] rounded-full"
                style={{ background: "radial-gradient(ellipse at center, rgba(0,0,0,0.62) 0%, rgba(0,0,0,0.28) 55%, transparent 78%)" }} />
              <div className="relative flex items-start justify-center font-bold leading-none tabular-nums tracking-tight text-[3.6rem] sm:text-8xl"
                style={{ filter: "drop-shadow(0 3px 14px rgba(0,0,0,0.55)) drop-shadow(0 0 26px rgba(216,178,110,0.22))" }}
                data-testid="ep3-countdown-numbers">
                {cells.map((c, i) => (
                  <Fragment key={c.label}>
                    {i > 0 && <span className="w-4 sm:w-6 text-center" style={goldText}>:</span>}
                    <span className="w-[4.5rem] sm:w-28 text-center" style={goldText} data-testid={c.tid}>{pad(c.value)}</span>
                  </Fragment>
                ))}
              </div>
              <div className="relative flex items-start justify-center mt-4">
                {cells.map((c, i) => (
                  <Fragment key={c.label}>
                    {i > 0 && <span className="w-4 sm:w-6 flex justify-center"><span className="w-px h-3.5 bg-[#d9c493]/50" /></span>}
                    <span className="w-[4.5rem] sm:w-28 text-center text-[10px] sm:text-[11px] uppercase tracking-[0.25em] text-[#d9c493]">{c.label}</span>
                  </Fragment>
                ))}
              </div>
            </div>
            <div className="font-didone text-sm sm:text-base uppercase tracking-[0.35em] mt-7 text-[#e8d9b0]" data-testid="ep3-sale-time"
              style={{ textShadow: "0 2px 16px rgba(0,0,0,0.7)" }}>
              Thursday · 12:00 AM CT
            </div>
          </div>
        )}

        {done ? (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            className="mt-10 border border-[#ff2d2d]/40 bg-black/70 backdrop-blur-xl px-8 py-8 rounded-2xl" data-testid="early-access-success"
            style={{ boxShadow: "0 0 24px rgba(255,45,45,0.18)" }}>
            <Check className="w-7 h-7 mx-auto mb-3" style={{ color: NEON }} />
            <div className="font-didone font-bold text-2xl text-[#FDFBF7] tracking-wide">You're on the list.</div>
            <p className="text-sm text-[#A19D94] mt-2">
              Early access lands in your inbox before tickets go public{smsOptIn && phone.trim() ? " — and we'll text you the moment they drop" : ""}.
            </p>
          </motion.div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.8 }}
            className="mt-10">
            <Divider tid="ep3-line-divider">Be First In Line</Divider>
            <form onSubmit={submit} className="mt-7 space-y-3" data-testid="early-access-form">
              <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required placeholder="Email address"
                className={inputCls} data-testid="early-access-email" />
              <input value={phone} onChange={(e) => setPhone(e.target.value)} type="tel" placeholder="Mobile number (optional)"
                className={inputCls} data-testid="early-access-phone" />
              <label className="flex items-start gap-3 text-left pt-1 cursor-pointer" data-testid="sms-consent-label">
                <input type="checkbox" checked={smsOptIn} onChange={(e) => setSmsOptIn(e.target.checked)}
                  className="mt-0.5 w-5 h-5 rounded-md border-white/30 bg-black/50 shrink-0" style={{ accentColor: NEON }}
                  data-testid="sms-consent-checkbox" />
                <span>
                  <span className="text-sm text-[#e8e2d5]">Text me ticket alerts from Jay ATK.</span>
                  <span className="block text-[11px] text-[#8f887a] mt-0.5">Optional. Msg &amp; data rates may apply. Reply STOP to unsubscribe.</span>
                  <span className="block text-[11px] text-[#8f887a] mt-1">
                    <span className="underline underline-offset-2">Privacy Policy</span> · <span className="underline underline-offset-2">Terms</span>
                  </span>
                </span>
              </label>
              <button type="submit" disabled={busy} data-testid="early-access-submit"
                className="w-full inline-flex items-center justify-center gap-2 rounded-2xl border-2 border-[#ff2d2d] bg-black/35 px-8 py-4 text-sm font-bold uppercase tracking-[0.4em] transition-all duration-300 hover:bg-[#ff2d2d]/15 disabled:opacity-50 mt-2"
                style={{ color: "#ff5a5a", textShadow: `0 0 10px ${NEON}, 0 0 26px ${NEON}`, boxShadow: "0 0 20px rgba(255,45,45,0.35), inset 0 0 16px rgba(255,45,45,0.14)" }}>
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Notify Me"}
              </button>
            </form>
          </motion.div>
        )}

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6, duration: 0.8 }} className="mt-12">
          <div className="h-px bg-white/15" />
          <div className="font-didone text-[#f3ead8] text-base sm:text-lg font-semibold tracking-[0.18em] mt-7" data-testid="ep3-date-line">
            Saturday, November 28 · 5 – 10 PM
          </div>
          <div className="h-px bg-white/10 w-2/3 mx-auto mt-7" />
          <p className="text-[13px] leading-6 tracking-[0.3em] text-[#d8cdb8] mt-7 uppercase">
            2617 Washington Ave<br />St. Louis, MO
          </p>
          <div className="inline-block border border-[#d8c9a8]/60 px-9 py-3.5 mt-7" data-testid="ep3-attire-badge">
            <div className="font-didone font-bold text-[#f3ead8] tracking-[0.15em]">SHADES OF BROWN</div>
            <div className="text-[10px] tracking-[0.35em] text-[#b3a88f] mt-1 uppercase">Attire Required</div>
          </div>
          <div className="mt-9 flex items-center justify-center gap-4">
            <a href="https://www.instagram.com/1jayatk" target="_blank" rel="noopener noreferrer" data-testid="coming-soon-instagram"
              className="w-11 h-11 border border-white/15 bg-black/40 backdrop-blur-sm flex items-center justify-center text-[#A19D94] hover:text-[#D4AF37] hover:border-[#D4AF37]/50 transition-colors duration-300" aria-label="Instagram">
              <Instagram className="w-4 h-4" />
            </a>
            <a href="https://www.facebook.com/share/1LHseGzdjw/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer" data-testid="coming-soon-facebook"
              className="w-11 h-11 border border-white/15 bg-black/40 backdrop-blur-sm flex items-center justify-center text-[#A19D94] hover:text-[#D4AF37] hover:border-[#D4AF37]/50 transition-colors duration-300" aria-label="Facebook">
              <Facebook className="w-4 h-4" />
            </a>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};
